import { useFormContext } from 'react-hook-form';
import { Bell, Pencil, X } from 'lucide-react';
import type { AppointmentFormValues } from '@/types/appointment-form';

interface ReminderSectionProps {
  /** Opens the full-screen Reminders picker (Flutter
   * `reminder_profile_selection_widget.dart`). */
  onEdit?: () => void;
}

/**
 * ReminderSection — selected reminder profile card.
 * Mirrors Flutter `reminder_profile_widget.dart`:
 *   - Bell icon + profile name (bold) + edit pencil + delete (×)
 *   - Bullet list of rules ("In-App 1 hour before", etc.) sourced from
 *     `reminderProfileType[]` on the picked ReminderProfile
 *   - Hidden when no reminder is set; the "Reminder" icon-button in the
 *     bottom action row re-opens the picker via `onOpenReminder`.
 */
export function ReminderSection({ onEdit }: ReminderSectionProps = {}) {
  const { watch, setValue } = useFormContext<AppointmentFormValues>();
  const reminderProfileId = watch('reminderProfileId');
  const reminderProfileName = watch('reminderProfileName');
  const reminderProfileRules = watch('reminderProfileRules');

  if (!reminderProfileId) return null;

  const clear = () => {
    setValue('reminderProfileId', null, { shouldValidate: true });
    setValue('reminderProfileName', null);
    setValue('reminderProfileRules', []);
  };

  return (
    <div className="px-4 py-3 border-t border-border-subtle">
      <p className="text-xs font-medium text-text-muted mb-2">
        Reminder
      </p>
      <div className="flex items-start gap-3 p-3 rounded-lg border border-border-default bg-surface-card">
        <Bell className="h-5 w-5 text-brand-primary flex-shrink-0 mt-1" />
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-text-primary truncate">
            {reminderProfileName || 'Reminder set'}
          </p>
          {reminderProfileRules.length > 0 ? (
            <ul className="mt-1 space-y-1">
              {reminderProfileRules.map((rule, i) => (
                <li
                  key={`${rule}-${i}`}
                  className="text-xs text-text-muted leading-snug before:content-['•'] before:mr-2 before:text-text-hint"
                >
                  {rule}
                </li>
              ))}
            </ul>
          ) : null}
        </div>
        {onEdit ? (
          <button
            type="button"
            onClick={onEdit}
            className="p-1 hover:bg-grey-100 rounded"
            aria-label="Edit reminder"
          >
            <Pencil className="h-4 w-4 text-brand-primary" />
          </button>
        ) : null}
        <button
          type="button"
          onClick={clear}
          className="p-1 hover:bg-grey-100 rounded"
          aria-label="Clear reminder"
        >
          <X className="h-4 w-4 text-status-error" />
        </button>
      </div>
    </div>
  );
}
